import React, { useCallback } from 'react';
import { useStyletron } from 'baseui';
import { useAtomValue } from 'jotai';
import { useDrawer } from '../../../../common/multi-level-drawer';
import { SIDE_PANEL_ACTIONS } from '../../../../modernization-common/side-panel/const';
import { useDispatch } from '../../../../modernization-common/side-panel/hooks/use-dispatch';
import { NavigateButton } from '../../../../modernization-common/side-panel-m4/remaining-items/v2/action-primitives';
import { RemainingItemsPanel } from '../../../../modernization-common/side-panel-m4/remaining-items/v2/remaining-items-panel';
import type { ActionRenderer, NavigationConfig, RemainingItem } from '../../../../modernization-common/side-panel-m4/remaining-items/v2/types';
import { OBJECT_TYPE } from '../../../../modernization-common/side-panel-m4/remaining-items/v2/types';
import { isVideoLabelingM4EnabledAtom } from '../../atoms/m4-atoms';
import { remainingItemsCountAtom, remainingItemsV2Atom } from '../../atoms/side-panel-atoms';
import { useOpenTaskQuestionnaireDrawer } from '../../hooks/use-open-task-questionnaire-drawer';
const REMAINING_ITEMS_DRAWER_ID = 'image-remaining-items';
const ImageRemainingItemsPanel = () => {
  const remainingItems = useAtomValue(remainingItemsV2Atom);
  const remainingItemsCount = useAtomValue(remainingItemsCountAtom);
  const isM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const dispatch = useDispatch();
  const {
    open,
    close
  } = useDrawer();
  const {
    openTaskQuestionnaireDrawer
  } = useOpenTaskQuestionnaireDrawer();
  const navigationConfig: NavigationConfig = {
    canNavigate: item => item.objectType === OBJECT_TYPE.ANNOTATION,
    onNavigate: useCallback((item: RemainingItem) => {
      if (item.objectType === OBJECT_TYPE.ANNOTATION) {
        // Non-M4 edits still render through the separate StatefulSideDrawer, so the
        // Remaining Items entry must be dismissed explicitly here. M4 edits
        // open on the same v2 stack, so closing here would drop remaining-items
        // instead of leaving it underneath.
        if (!isM4Enabled) {
          close();
        }
        dispatch({
          type: SIDE_PANEL_ACTIONS.EDIT_ANNOTATION_START,
          annotationId: item.id
        });
      }
    }, [close, dispatch, isM4Enabled])
  };
  const renderItemActions: ActionRenderer = useCallback((item: RemainingItem) => {
    if (item.objectType === OBJECT_TYPE.TASK_QUESTIONNAIRE) {
      const handleOpenTaskQuestionnaire = () => {
        // Non-M4 opens the task questionnaire on the separate v1 StatefulSideDrawer, so
        // the Remaining Items entry (on the v2 stack) must be dismissed explicitly here.
        // M4 opens on the same v2 stack, so closing here would drop Remaining Items
        // instead of leaving it underneath.
        if (!isM4Enabled) {
          close();
        }
        openTaskQuestionnaireDrawer();
      };
      return <NavigateButton label="Task Questionnaire" onNavigate={handleOpenTaskQuestionnaire} />;
    }
    return null;
  }, [close, isM4Enabled, openTaskQuestionnaireDrawer]);
  return <RemainingItemsPanel items={remainingItems} itemCount={remainingItemsCount} navigationConfig={navigationConfig} renderItemActions={renderItemActions} />;
};
export const useOpenRemainingItemsDrawer = () => {
  const {
    open
  } = useDrawer();
  const remainingItemsCount = useAtomValue(remainingItemsCountAtom);
  const [, theme] = useStyletron();
  const openRemainingItemsDrawer = useCallback(() => {
    open({
      id: REMAINING_ITEMS_DRAWER_ID,
      title: `Remaining Items (${remainingItemsCount})`,
      description: 'Resolve these to submit the task',
      content: () => <ImageRemainingItemsPanel />,
      styleOverrides: {
        content: {
          padding: theme.sizing.scale500,
          paddingTop: 0
        }
      }
    });
  }, [open, remainingItemsCount, theme]);
  return {
    openRemainingItemsDrawer
  };
};